import { ChevronRight, Cloud, FolderOpen, HardDrive, ImagePlus, Link, Trash2, Video, type LucideIcon } from 'lucide-react';
import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { deleteLocalFile, saveLocalFile } from '../../lib/localMedia';
import { useSession } from '../../store/session';
import { newId, useSettings, type MediaItem } from '../../store/settings';
import { buttonClass, ghostButtonClass, inputClass, Section } from './controls';
import { OneDrivePicker } from './OneDrivePicker';

const VIDEO_EXT = /\.(mp4|webm|mov|m4v)(\?|#|$)/i;

function kindFor(url: string, mime?: string): MediaItem['kind'] {
  if (mime) return mime.startsWith('video/') ? 'video' : 'image';
  return VIDEO_EXT.test(url) ? 'video' : 'image';
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Removes items from the slideshow; device files are also deleted from this screen's storage. */
function useRemoveItem() {
  const update = useSettings((s) => s.update);
  return (item: MediaItem) => {
    if (item.local) {
      if (item.url) URL.revokeObjectURL(item.url);
      void deleteLocalFile(item.id).catch(() => undefined);
    }
    update('media', { items: useSettings.getState().media.items.filter((i) => i.id !== item.id) });
  };
}

function MediaGrid({ items, empty }: { items: MediaItem[]; empty: string }) {
  const remove = useRemoveItem();
  if (!items.length) return <p className="muted text-sm">{empty}</p>;
  return (
    <ul className="grid grid-cols-3 gap-2">
      {items.map((item) => (
        <li key={item.id} className="group relative aspect-video overflow-hidden rounded-lg bg-black/40">
          {item.kind === 'image' ? (
            <img src={item.url} alt={item.title ?? ''} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Video className="h-6 w-6 opacity-70" aria-hidden />
            </div>
          )}
          <button
            onClick={() => remove(item)}
            className="absolute right-1 top-1 rounded-md bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100 focus:opacity-100"
            aria-label="Remove"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Settings → Media: where the photos come from, with a link to each kind of source. */
export function MediaOverview({ go }: { go: (tab: string) => void }) {
  const media = useSettings((s) => s.media);
  const microsoft = useSession((s) => s.microsoft);
  const links = media.items.filter((i) => !i.local).length;
  const files = media.items.filter((i) => i.local).length;
  const folders = media.cloudFolders.length;
  const nothing = !links && !files && !folders;

  const rows: Array<{ id: string; name: string; icon: LucideIcon; summary: string }> = [
    {
      id: 'cloud',
      name: 'Cloud storage',
      icon: Cloud,
      summary: folders ? `OneDrive · ${plural(folders, 'folder')}` : microsoft?.connected ? 'Microsoft connected · no folders yet' : 'Microsoft, Google, Amazon, iCloud, Dropbox',
    },
    { id: 'links', name: 'Public links', icon: Link, summary: links ? plural(links, 'link') : 'Photos and videos anyone can open on the web' },
    { id: 'local', name: 'Local & network', icon: HardDrive, summary: files ? `${plural(files, 'file')} on this screen` : 'Files and folders from this device or a network drive' },
  ];

  return (
    <>
      <Section
        title="Your photos & videos"
        description={nothing ? 'Nothing chosen yet, so the demo video plays (or the ambient scenes, if it’s switched off).' : 'Everything below joins one slideshow.'}
      >
        <ul className="space-y-2">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => go(r.id)}
                className="flex w-full items-center gap-3 rounded-xl border border-[var(--border)] p-3 text-left transition hover:border-[var(--fg-muted)]"
              >
                <r.icon className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{r.name}</span>
                  <span className="muted block truncate text-xs">{r.summary}</span>
                </span>
                <ChevronRight className="h-5 w-5 shrink-0" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      </Section>
      <p className="muted text-sm">
        Time per slide, transitions and shading are in{' '}
        <button type="button" className="text-[var(--accent)] hover:underline" onClick={() => go('slideshow')}>
          Appearance → Slideshow
        </button>
        .
      </p>
    </>
  );
}

function ProviderCard({ name, detail, status, children }: { name: string; detail: string; status: string; children: ReactNode }) {
  const live = status === 'Connected';
  return (
    <li className="rounded-xl border border-[var(--border)] p-4">
      <div className="mb-2 flex items-center gap-3">
        <Cloud className="h-5 w-5 shrink-0 opacity-80" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{name}</div>
          <div className="muted text-xs">{detail}</div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs ${live ? 'bg-[var(--accent)] text-black' : 'border border-[var(--border)] text-[var(--fg-muted)]'}`}
        >
          {status}
        </span>
      </div>
      <div className="text-sm">{children}</div>
    </li>
  );
}

/** Settings → Media → Cloud storage. Only Microsoft can be connected today; the others say why not (yet). */
export function CloudStorageTab() {
  const { config, microsoft } = useSession();
  const msStatus = !config?.features.microsoft ? 'Unavailable' : microsoft?.connected ? 'Connected' : 'Available';
  return (
    <Section
      title="Cloud storage providers"
      description="Show photos and videos from folders in your cloud storage. They stay where they are: KCL Canvas reads them when they’re shown."
    >
      <ul className="space-y-3">
        <ProviderCard name="Microsoft" detail="OneDrive and SharePoint" status={msStatus}>
          {config?.features.microsoft ? <OneDrivePicker /> : <p className="muted">Microsoft sign-in isn’t switched on for this site yet.</p>}
        </ProviderCard>
        <ProviderCard name="Google" detail="Google Drive and Google Photos" status="Coming soon">
          <p className="muted">
            Google Drive folders are planned. Google now only lets apps show Google Photos that you pick one by one, rather than
            whole albums.
          </p>
        </ProviderCard>
        <ProviderCard name="Amazon" detail="Amazon Photos" status="Not available">
          <p className="muted">Amazon doesn’t let other apps read Amazon Photos. Download them and add them from Local &amp; network.</p>
        </ProviderCard>
        <ProviderCard name="iCloud" detail="iCloud Photos" status="Not available">
          <p className="muted">
            Apple doesn’t let other apps read iCloud Photos. On a Windows PC with iCloud for Windows, add your iCloud Photos
            folder from Local &amp; network. iCloud calendars do work: Widgets → Calendar.
          </p>
        </ProviderCard>
        <ProviderCard name="Dropbox" detail="Dropbox folders" status="Coming soon">
          <p className="muted">Dropbox folders are planned.</p>
        </ProviderCard>
      </ul>
    </Section>
  );
}

/** Settings → Media → Public links: photos and videos anyone can open on the web. */
export function PublicLinksTab() {
  const items = useSettings((s) => s.media.items);
  const update = useSettings((s) => s.update);
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const addUrl = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    let parsed: URL;
    try {
      parsed = new URL(url.trim());
    } catch {
      setError('That doesn’t look like a web address.');
      return;
    }
    if (parsed.protocol !== 'https:') {
      setError('Use an https:// address.');
      return;
    }
    update('media', { items: [...items, { id: newId(), url: parsed.toString(), kind: kindFor(parsed.pathname) }] });
    setUrl('');
  };

  return (
    <Section
      title="Public files & folders"
      description="The web address of a photo or video that anyone can open, such as a picture on a website. Links to whole shared folders aren’t supported yet."
    >
      <form onSubmit={addUrl} className="flex gap-2">
        <input
          className={inputClass}
          placeholder="https://… (image, .mp4, .webm or .mov)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label="Media address"
        />
        <button className={buttonClass} type="submit">
          Add
        </button>
      </form>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <MediaGrid items={items.filter((i) => !i.local)} empty="No links added yet." />
    </Section>
  );
}

/** Settings → Media → Local & network: files and folders from this device (including mapped network drives). */
export function LocalSourcesTab() {
  const items = useSettings((s) => s.media.items);
  const update = useSettings((s) => s.update);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);

  const addFiles = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter((f) => /^(image|video)\//.test(f.type));
    e.target.value = '';
    setError(null);
    const added: MediaItem[] = [];
    let unsaved = 0;
    for (const f of files) {
      const id = newId();
      try {
        await saveLocalFile(id, f);
      } catch {
        unsaved++; // quota or private mode – still show it for this session
      }
      added.push({ id, url: URL.createObjectURL(f), kind: kindFor(f.name, f.type), title: f.name, local: true });
    }
    update('media', { items: [...useSettings.getState().media.items, ...added] });
    if (unsaved) setError(`${unsaved} file(s) couldn’t be stored on this device and will disappear after a reload.`);
  };

  return (
    <>
      <Section
        title="This device"
        description="Photos and videos from this computer, tablet or phone. They’re kept on this screen only and never uploaded."
      >
        <div className="flex flex-wrap gap-2">
          <button className={ghostButtonClass} onClick={() => fileInput.current?.click()} type="button">
            <ImagePlus className="h-4 w-4" aria-hidden /> Add files
          </button>
          <button className={ghostButtonClass} onClick={() => folderInput.current?.click()} type="button">
            <FolderOpen className="h-4 w-4" aria-hidden /> Add a folder
          </button>
          <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={addFiles} />
          <input
            ref={(el) => {
              folderInput.current = el;
              el?.setAttribute('webkitdirectory', '');
            }}
            type="file"
            multiple
            hidden
            onChange={addFiles}
          />
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <MediaGrid items={items.filter((i) => i.local)} empty="No files added on this screen yet." />
      </Section>
      <Section title="Network drives & NAS">
        <p className="muted text-sm">
          On a computer, a network drive or NAS that’s connected as a drive appears in the file picker: use <strong>Add a folder</strong>{' '}
          and choose it. The photos are copied onto this screen, so pictures added to the drive later need adding again.
        </p>
        <p className="muted text-sm">TVs can’t open network drives directly yet.</p>
      </Section>
    </>
  );
}
